import { Box, Card, Typography } from "@mui/material";
import { useState } from "react";
import theme from "../../theme";
import { ProductDraft } from "../../context/ProductContext";
import ReactMarkdown from 'react-markdown';

interface ProductCardProps {
  product: ProductDraft;
  /** toggles layout between image/text arrangement */
  layout?: "row" | "column";
  /** allow content to wrap onto the next line in row mode */
  wrap?: boolean;
}

type AspectRatio = "16 / 9" | "4 / 3" | "1 / 1" | "3 / 4";

const getHost = (url?: string) => {
  try {
    if (!url) return "";
    const { hostname } = new URL(url);
    return hostname.replace(/^www\./, "");
  } catch {
    return "";
  }
};

const pickAspect = (w: number, h: number, host: string): AspectRatio => {
  if (host === 'youtube.com') return "16 / 9"
  if (!w || !h) return "16 / 9";
  const r = w / h;
  const candidates: Array<[AspectRatio, number]> = [
    ["16 / 9", 16 / 9],
    ["4 / 3", 4 / 3],
    ["1 / 1", 1],
    ["3 / 4", 3 / 4],
  ];
  let best: AspectRatio = candidates[0][0];
  let bestDiff = Infinity;
  for (const [label, val] of candidates) {
    const diff = Math.abs(r - val);
    if (diff < bestDiff) {
      bestDiff = diff;
      best = label;
    }
  }
  return best;
};

const ProductCard: React.FC<ProductCardProps> = ({
  product,
  layout = "row",
  wrap = false,
}) => {
  const host = getHost(product?.productURL);
  const isRow = layout === "row";
  const [aspect, setAspect] = useState<AspectRatio>("16 / 9");

  return (
    <Card
      elevation={3}
      sx={{
        width: "100%",
        maxWidth: "fit-content",
        height: '100%',
        minHeight: 'fit-content',
        aspectRatio: isRow && !wrap ? '25 / 6' : '4 / 5',
        p: 1,
        borderRadius: 4,
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: isRow ? "row" : "column",
          flexWrap: wrap && isRow ? "wrap" : "nowrap",
          gap: 2,
          alignItems: "stretch",
          height: 1,
          // critical so children can actually shrink and text ellipsis works
          minWidth: 0,
        }}
      >
        {!!product?.imageURL && (
          <Box
            sx={{
              // ROW + no-wrap: fixed height, intrinsic width by aspect
              // COLUMN or WRAP: full width, 16:9
              flex: (!isRow || wrap) ? "1 1 100%" : "0 0 auto",
              width: (!isRow || wrap) ? "100%" : "auto",
              height: (!isRow || wrap) ? "auto" : 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",

              overflow: "hidden",
              borderRadius: 3,
              bgcolor: theme.palette.action.hover,
              border: `1px solid ${theme.palette.divider}`,
              p: 0.5,

              /**
               * In column OR wrap:
               *  - force the inner aspect box to be width-driven 16:9
               *  - do NOT touch the internal image structuring otherwise
               */
              ...((!isRow || wrap) && {
                "& > .inner-aspect": {
                  width: "100%",
                  height: "auto",
                  aspectRatio: "16 / 9",
                  maxWidth: "100%",
                },
              }),
            }}
          >
            {/* Inner aspect box (kept as requested). 
                Row (no-wrap): fixed height parent, width = height * aspect.
                Column/Wrap: overridden to width:100% + aspectRatio:16/9 via parent selector above. */}
            <Box
              className="inner-aspect"
              sx={{
                height: 1,                 // fill fixed-height wrapper in row mode
                aspectRatio: aspect,       // dynamic buckets in row mode
                borderRadius: 2,
                overflow: "hidden",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <img
                src={product.imageURL}
                alt={product?.name || "thumbnail"}
                onLoad={(e) => {
                  const img = e.currentTarget;
                  // Only matters in row mode; in column/wrap we force 16:9 on the wrapper
                  setAspect(pickAspect(img.naturalWidth, img.naturalHeight, host));
                }}
                onError={() => setAspect("16 / 9")}
                style={{
                  width: host === 'youtube.com' ? '100%' :'auto',
                  height: "100%",
                  objectFit: "cover", // keeps a clean, bar-free frame
                  display: "block",
                  borderRadius: 6,
                }}
              />
            </Box>
          </Box>
        )}

        {/* Text block */}
        <Box
          sx={{
            // take remaining width, allow shrink
            flex: "1 1 0",
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            textAlign: "left",
            px: isRow && !wrap ? 0 : 1,
            justifyContent: "space-between",
            gap: 1,
            // ensure in wrap mode it occupies a new line beneath the image
            ...(wrap && isRow ? { flexBasis: "100%" } : {}),
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              // variant "h7" isn't a native MUI variant; use custom sizing
              sx={{
                fontWeight: 700,
                lineHeight: 1.2,
                fontSize: "0.9rem",
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
              }}
            >
              {product?.name || ""}
            </Typography>

            <Typography
              variant="body2"
              component="div"
              sx={{
                fontSize: "0.75rem",
                lineHeight: 1.2,
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                // Ensure markdown content respects overflow constraints
                '& p': {
                  margin: 0,
                  display: 'inline',
                },
                '& *': {
                  display: 'inline',
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                },
              }}
            >
              <ReactMarkdown>{product?.description || ""}</ReactMarkdown>
            </Typography>
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, minWidth: 0 }}>
            {product?.authors && (
              <Typography
                sx={{
                  color: theme.palette.text.primary,
                  fontSize: "0.95rem",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {product.authors}
              </Typography>
            )}

            {host && (
              <Typography
                sx={{
                  fontSize: "0.875rem",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                variant="body2"
              >
                {host}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    </Card>
  );
};

export default ProductCard;
